import _, { isObject } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  myTasksAuditQuestionsListName,
  myTasksAuditWithRelationsListName,
} from "src/api/config/myTasksAudit.config";
import {
  MTFavoriteFiltersListName,
  myTasksDepartmentsListName,
  myTasksDependListName,
  myTasksLocationsListName,
  myTasksRegionsListName,
} from "src/api/config/myTasksLists.config";
import Form from "src/components/Form";
import Loading from "src/components/Loading";
import MyTasksAuditsTable from "src/components/tables/MyTasksAuditsTable.mobile";
import { SearchUI } from "src/components/ui/Search";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItemsRequest } from "src/modules/spItems/api";
import { selectItems } from "src/modules/spItems/selectors";
import { addUsers } from "src/modules/users/actions";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { RootState } from "src/store/rootReducer";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { auditKeys } from "../../throughcomponents/auditCard/constants";
import AddAudit from "../addAudit/addAudit.mobile";
import LoadingOpBg from "src/components/LoadingOpBg";
import FilterActions from "src/components/filter-actions/filter-actions.mobile";
import FiltersButton from "src/components/filters-button/filters-button";

function normalizeEmailLike(raw: string): string {
  let s = raw.trim();
  if (s.includes("|")) s = s.split("|")[s.split("|").length - 1] || s;
  if (s.includes("-deleted")) s = s.split("-deleted")[0] || s;
  if (s.toLowerCase().startsWith("mailto:")) s = s.slice("mailto:".length);
  return s.trim().toLowerCase();
}

function safeStorageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage?.getItem(key) ?? window.sessionStorage?.getItem(key) ?? null;
  } catch {
    try {
      return window.sessionStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }
}

function safeStorageSet(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    try {
      window.sessionStorage?.setItem(key, value);
    } catch {
      // ignore
    }
  }
}

function normalizeSearchValue(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

function safeLookupValue(value: any): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.get_lookupValue === "function") {
    try {
      return value.get_lookupValue() || "";
    } catch {
      return "";
    }
  }
  if (typeof value.Title === "string") return value.Title;
  if (typeof value.name === "string") return value.name;
  return "";
}

function safeResolvedUserName(item: any, emailField: string, fallbackField: string, users: any): string {
  const rawEmail = item?.[emailField];
  if (typeof rawEmail === "string" && rawEmail.trim()) {
    const normalizedEmail = normalizeEmailLike(rawEmail);
    const user = users?.[normalizedEmail];
    if (user?.displayName || user?.fullName) {
      return user.displayName || user.fullName;
    }
  }
  const fallback = safeLookupValue(item?.[fallbackField]);
  if (!fallback || /^\d+$/.test(fallback.trim())) return "";
  return fallback;
}

function getAuditSearchHaystack(item: any, users: any): string {
  const observerName = safeResolvedUserName(item, "ObserverEmail", "Observer", users);
  const observedName =
    (item?.ObservedType === "Внутренний" &&
      safeResolvedUserName(item, "ObservedUserEmail", "ObservedUser", users)) ||
    item?.ObservedString ||
    "";
  const location = safeLookupValue(item?.Location);
  const department = safeLookupValue(item?.Department);
  const region = safeLookupValue(item?.Region);

  return normalizeSearchValue(
    [
      item?.Description || "",
      observerName,
      observedName,
      moment(item?.DateOb).isValid() ? moment(item.DateOb).format("DD.MM.YYYY") : "",
      location,
      department,
      region,
      [location, department, region].filter(Boolean).join("/"),
      item?.actionsPlan ? "действия запланированы да" : "действия запланированы нет",
    ].join(" ")
  );
}

function AuditsFilters({
  searchQuery,
  setFilters,
  setSearchQuery,
  setGegAudits,
  filters,
  setLocalFilters,
  currentUser,
  setDisplayFilters,
  filtersParams,
  setFiltersParams,
  defaultUserFilter,
  userFilterCheck,
  reRenderUserFilter,
  handleResetAllFilters,
  auditsFilter,
  setAuditsFilter,
  resetFilters,
  setResetFilters
}: any) {
  const [defaultValues, setDefaultValues] = useState({});
  const [auditQuestions, setAuditQuestions] = useState<any>([]);
  const [fields, setFields] = useState<any>([
    {
      type: "dateRange",
      require: false,
      key: "DateOb",
      isFilter: true,
      title: "Дата наблюдения",
      placeholder: "дд.мм.гггг",
      width: "w-full",
    },
    {
      type: "peoplePicker",
      require: false,
      single: true,
      key: "Observer",
      isFilter: true,
      title: "ФИО наблюдателя",
      current: false,
      placeholder: "Введите имя или фамилию",
      width: "w-full",
    },
    {
      type: "peoplePicker",
      require: false,
      single: true,
      key: "ObservedUser",
      isFilter: true,
      title: "ФИО, за кем проводили наблюдение",
      current: false,
      placeholder: "Введите имя или фамилию",
      width: "w-full",
    },
    {
      type: "checkbox",
      require: false,
      key: "Location",
      isFilter: true,
      title: "Локация, где проводилось наблюдение",
      condition: null,
      width: "w-full",
    },
    {
      type: "checkbox",
      require: false,
      key: "Department",
      isFilter: true,
      title: "Отдел, где проводилось наблюдение",
      condition: null,
      width: "w-full",
    },
    {
      type: "checkbox",
      require: false,
      key: "Region",
      isFilter: true,
      title: "Участок, где проводилось наблюдение",
      condition: null,
      width: "w-full",
    }
  ]);
  const [renderAudit, setRenderAudit] = useState(false);
  const collections: any = useAppSelector((state) =>
    selectItems(state, myTasksLocationsListName)
  );
  const departments: any = useAppSelector((state) =>
    selectItems(state, myTasksDepartmentsListName)
  );
  const sectors: any = useAppSelector((state) =>
    selectItems(state, myTasksRegionsListName)
  );
  const [dependenceFields, setDependenceFields] = useState<any>({
    Location: null,
    Department: null,
    Region: null,
  });
  const dispatch = useAppDispatch();
  const [goldenRule, setGoldenRule] = useState("");
  const [customLoading, setCustomLoading] = useState(true);

  useEffect(() => {
    if (renderAudit) {
      setCustomLoading(true);
      const tempFieldsForm = [...fields];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]["key"] === "Location") {
          tempFieldsForm[i]["optionsInputs"] = collections.map(
            (collection: any) => {
              return {
                id: collection.ID,
                name: collection.Title,
                disabled: false,
              };
            }
          );
        }
        if (tempFieldsForm[i]["key"] === "Department") {
          tempFieldsForm[i]["optionsInputs"] = departments.map(
            (department: any) => {
              return {
                id: department.ID,
                name: department.Title,
                disabled: false,
              };
            }
          );
        }
        if (tempFieldsForm[i]["key"] === "Region") {
          tempFieldsForm[i]["optionsInputs"] = sectors.map((sector: any) => {
            return {
              id: sector.ID,
              name: sector.Title,
              disabled: false,
            };
          });
        }
      }
      setFields([...tempFieldsForm]);
      setCustomLoading(false);
    }
  }, [collections, departments, sectors, renderAudit]);

  useEffect(() => {
    if (renderAudit) {
      setCustomLoading(true);
      let filtersDependDocuments: any = {};
      const tempFieldsForm = [...fields];
      Object.keys(dependenceFields).map((key) => {
        for (let i = 0; i < tempFieldsForm.length; i++) {
          if (tempFieldsForm[i]["key"] === key) {
            tempFieldsForm[i]["optionsInputs"] = tempFieldsForm[i][
              "optionsInputs"
            ].map((option: any) => {
              return {
                id: option.id,
                name: option.name,
                disabled: false,
              };
            });
          }
        }
      });
      Object.keys(dependenceFields).map((key) => {
        if (dependenceFields[key] && dependenceFields[key].length > 0) {
          filtersDependDocuments[key] = dependenceFields[key].map(
            (item: any) => item.id
          );
        }
      });
      if (Object.keys(filtersDependDocuments).length === 0) {
        setFields([...tempFieldsForm]);
        setCustomLoading(false);
      } else {
        if (Object.keys(filtersDependDocuments).length > 0) {
          //Устанавливаем зависимости в фильтрах
          getItemsRequest({
            listName: myTasksDependListName,
            filters: filtersDependDocuments,
            filtersType: "or",
          }).then((results) => {
            const dependDocuments = [...results.items];
            const collectionIds = _.uniqWith(
              dependDocuments.map((item: any) => item.Location.get_lookupId()),
              _.isEqual
            );
            const departmentIds = _.uniqWith(
              dependDocuments.map((item: any) =>
                item.Departament.get_lookupId()
              ),
              _.isEqual
            );
            const sectorIds = _.uniqWith(
              dependDocuments.map((item: any) => item.Region.get_lookupId()),
              _.isEqual
            );
            for (let i = 0; i < tempFieldsForm.length; i++) {
              if (tempFieldsForm[i]["key"] === "Location") {
                if (
                  !filtersDependDocuments["Location"] ||
                  Object.keys(filtersDependDocuments).length > 1
                ) {
                  tempFieldsForm[i]["optionsInputs"] = collections.map(
                    (collection: any) => {
                      return {
                        id: collection.ID,
                        name: collection.Title,
                        disabled: false, //collectionIds.indexOf(collection.ID) === -1,
                        hide: collectionIds.indexOf(collection.ID) === -1,
                      };
                    }
                  );
                }
              }
              if (tempFieldsForm[i]["key"] === "Department") {
                if (
                  !filtersDependDocuments["Department"] ||
                  Object.keys(filtersDependDocuments).length > 1
                ) {
                  tempFieldsForm[i]["optionsInputs"] = departments.map(
                    (department: any) => {
                      return {
                        id: department.ID,
                        name: department.Title,
                        disabled: false, //departmentIds.indexOf(department.ID) === -1,
                        hide: departmentIds.indexOf(department.ID) === -1,
                      };
                    }
                  );
                }
              }
              if (tempFieldsForm[i]["key"] === "Region") {
                if (
                  !filtersDependDocuments["Region"] ||
                  Object.keys(filtersDependDocuments).length > 1
                ) {
                  tempFieldsForm[i]["optionsInputs"] = sectors.map(
                    (sector: any) => {
                      return {
                        id: sector.ID,
                        name: sector.Title,
                        disabled: false, //sectorIds.indexOf(sector.ID) === -1,
                        hide: sectorIds.indexOf(sector.ID) === -1,
                      };
                    }
                  );
                }
              }
            }
            setFields([...tempFieldsForm]);
            setCustomLoading(false);
          });
        }
      }
    }
  }, [dependenceFields, renderAudit]);

  useEffect(() => {
    if (auditQuestions.length > 0) {
      setFields((prevState: []) => [...prevState, ...auditQuestions]);
      setCustomLoading(false);
      setRenderAudit(true);
    }
  }, [auditQuestions]);

  useEffect(() => {
    getItemsRequest({ listName: myTasksAuditQuestionsListName }).then(
      (results) => {
        const tempAuditQuestions = [...results.items];
        let auditFieldsForm: any = [];
        tempAuditQuestions.forEach((item) => {
          auditFieldsForm = [
            ...auditFieldsForm,
            {
              type: item.TypeQuestion,
              require: false,
              key: item.dc0t,
              title: item.Title,
              hide: item.DefaultHide,
              width: "w-full",
              isFilter: true,
              optionsInputs:
                (item.TypeQuestion === "checkbox" &&
                  item.Options &&
                  item.Options.split(";").map((value: any) => {
                    return {
                      id: value,
                      name: value,
                      disabled: false,
                    };
                  })) ||
                (item.Options &&
                  item.Options.split(";").map((option) => option.trim())) ||
                [],
            },
          ];
        });
        setAuditQuestions([...auditFieldsForm]);
      }
    );
  }, []);

  useEffect(() => {
    if (goldenRule && renderAudit) {
      setCustomLoading(true);
      const tempFieldsForm = [...fields];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]["key"] === "AGR") {
          if (goldenRule === "Да") {
            tempFieldsForm[i]["hide"] = false;
          }
          if (goldenRule === "Нет") {
            tempFieldsForm[i]["hide"] = true;
          }
        }
      }
      setFields([...tempFieldsForm]);
      setCustomLoading(false);
    }
  }, [goldenRule, renderAudit]);

  useEffect(() => {
    if (renderAudit && collections.length > 0 && Object.keys(currentUser).length > 0 && currentUser.id > 0) {
      let defaultObj: any = {};
      const today = moment();
      const isMonday = today.isoWeekday() === 1;
      if (Object.keys(filtersParams).length > 0) {
        Object.keys(filtersParams).forEach(key => {
          if (filtersParams[key]) {
            defaultObj[key] = filtersParams[key];
          } else {
            defaultObj[key] = null;
          }
        });
        defaultObj['DateOb'] = filtersParams['DateOb'] || (!defaultUserFilter && auditsFilter && auditsFilter['DateOb']) || {
          startDate: isMonday && moment().subtract(1, "weeks").startOf("week").format('YYYY-MM-DDT00:00:00') || moment().startOf("week").format('YYYY-MM-DDT00:00:00'),
          endDate: isMonday && moment().subtract(1, "weeks").endOf("week").format('YYYY-MM-DDT23:59:59') || moment().endOf("week").format('YYYY-MM-DDT23:59:59')
        };
        if (!_.isEqual(defaultValues, defaultObj)) {
          setDefaultValues(defaultObj);
        }
      } else if (defaultUserFilter && Object.keys(JSON.parse(defaultUserFilter.MTFullFilters)).length > 0) {
        const parsedDefault = JSON.parse(defaultUserFilter.MTFullFilters);
        Object.keys(parsedDefault).forEach(key => {
          if (parsedDefault[key]) {
            defaultObj[key] = parsedDefault[key];
          } else {
            defaultObj[key] = null;
          }
        });
        defaultObj['DateOb'] = parsedDefault['DateOb'] || {
          startDate: isMonday && moment().subtract(1, "weeks").startOf("week").format('YYYY-MM-DDT00:00:00') || moment().startOf("week").format('YYYY-MM-DDT00:00:00'),
          endDate: isMonday && moment().subtract(1, "weeks").endOf("week").format('YYYY-MM-DDT23:59:59') || moment().endOf("week").format('YYYY-MM-DDT23:59:59')
        };
        if (!_.isEqual(defaultValues, defaultObj)) {
          setDefaultValues(defaultObj);
        }
      } else {
        let defaultLocationsIds: number[] = [];
        if (currentUser.isLocationAdminMyTasks || currentUser.isLocationEditorMyTasks) {
          if (Array.isArray(currentUser.isLocationAdminMyTasks) && currentUser.isLocationAdminMyTasks.length > 0) {
            defaultLocationsIds = [...defaultLocationsIds, ...currentUser.isLocationAdminMyTasks];
          }
          if (Array.isArray(currentUser.isLocationEditorMyTasks) && currentUser.isLocationEditorMyTasks.length > 0) {
            defaultLocationsIds = [...defaultLocationsIds, ...currentUser.isLocationEditorMyTasks];
          }
        }
        let unicDefaultLocationsIds: number[] = [..._.uniqWith(defaultLocationsIds, _.isEqual)];
        setDependenceFields({
          Location: unicDefaultLocationsIds.length > 0 && unicDefaultLocationsIds.map(id => {
            return { id: id, name: collections.find((collection: any) => collection.ID === id)['Title'], disabled: false }
          }) || null,
          Department: null,
          Region: null,
        });
        fields.forEach((field: any) => {
          if (field.key === "DateOb") {
            defaultObj[field.key] = (auditsFilter && auditsFilter[field.key]) || {
              startDate: isMonday && moment().subtract(1, "weeks").startOf("isoWeek").format('YYYY-MM-DDT00:00:00') || moment().startOf("isoWeek").format('YYYY-MM-DDT00:00:00'),
              endDate: isMonday && moment().subtract(1, "weeks").endOf("isoWeek").format('YYYY-MM-DDT23:59:59') || moment().endOf("isoWeek").format('YYYY-MM-DDT23:59:59')
            };
          } else if (field.key === "Location") {
            defaultObj[field.key] = unicDefaultLocationsIds.length > 0 && unicDefaultLocationsIds.map(id => {
              return { id: id, name: collections.find((collection: any) => collection.ID === id)['Title'], disabled: false }
            }) || null;
          } else {
            defaultObj[field.key] = null;
          }
        });
        if (!_.isEqual(defaultValues, defaultObj)) {
          setDefaultValues(defaultObj);
        }
      }
    }
  }, [resetFilters, renderAudit, collections, JSON.stringify(currentUser), reRenderUserFilter]);

  useEffect(() => {
    if (auditsFilter && Object.keys(auditsFilter).length > 0) {
      const existsFiltersAudit: any = {};
      Object.keys(auditsFilter).map((key) => {
        if (auditsFilter[key]) {
          if (Array.isArray(auditsFilter[key])) {
            if (auditsFilter[key].length > 0) {
              existsFiltersAudit[key] = auditsFilter[key];
            }
          } else if (isObject(auditsFilter[key])) {
            if (Object.keys(auditsFilter[key]).length > 0) {
              existsFiltersAudit[key] = auditsFilter[key];
            }
          } else {
            existsFiltersAudit[key] = auditsFilter[key];
          }
        }
      });
      console.log("existsFiltersAudit: ", existsFiltersAudit);
      const tempFilters: any = {};
      const tempDisplayFilters: any = {};
      let tempAuditQuestions: any = [];
      let tempLocalAuditQuestions: any = {};
      Object.keys(existsFiltersAudit).map((key) => {
        if (auditKeys.some((auditKey: string) => auditKey === key)) {
          if (Array.isArray(existsFiltersAudit[key])) {
            if (existsFiltersAudit[key].length > 0) {
              if (
                existsFiltersAudit[key].some(
                  (item) =>
                    typeof item === "object" && item.hasOwnProperty("name")
                )
              ) {
                existsFiltersAudit[key].sort((a: any, b: any) => {
                  return a.name.localeCompare(b.name);
                });
              } else {
                existsFiltersAudit[key].sort((a: any, b: any) => {
                  return a.localeCompare(b);
                });
              }
              tempLocalAuditQuestions = {
                ...tempLocalAuditQuestions,
                ...{ [key]: existsFiltersAudit[key] },
              };
              tempDisplayFilters[key] = existsFiltersAudit[key].map((item => item.name || item));
            }
          } else {
            tempAuditQuestions = [
              ...tempAuditQuestions,
              `"${key}":${`"${existsFiltersAudit[key]}"`}`,
            ];
            tempDisplayFilters[key] = existsFiltersAudit[key];
          }
        } else if (key === "DateOb") {
          if (
            moment(existsFiltersAudit[key].startDate).isValid() &&
            moment(existsFiltersAudit[key].endDate).isValid()
          ) {
            tempFilters["DateOb"] = {
              start: moment(existsFiltersAudit[key].startDate).startOf('day').format('YYYY-MM-DDT00:00:00'),
              end: moment(existsFiltersAudit[key].endDate).endOf('day').format('YYYY-MM-DDT23:59:59'),
            };
            tempDisplayFilters[key] = `от ${moment(existsFiltersAudit[key].startDate).format('DD.MM.YYYY')} до ${moment(existsFiltersAudit[key].endDate).format('DD.MM.YYYY')}`;
          }
        } else if (key === "Observer") {
          tempFilters["ObserverEmail"] = existsFiltersAudit[key][0]["value"];
          tempDisplayFilters[key] = existsFiltersAudit[key][0]["label"];
        } else if (key === "ObservedUser") {
          tempFilters["ObservedUserEmail"] = existsFiltersAudit[key][0]["value"];
          tempDisplayFilters["ObservedUserEmail"] = existsFiltersAudit[key][0]["label"];
          tempFilters["ObservedType"] = "Внутренний";
        } else if (
          key === "Location" ||
          key === "Department" ||
          key === "Region"
        ) {
          tempFilters[key] = existsFiltersAudit[key].map(
            (item: any) => item.id
          );
          tempDisplayFilters[key] = existsFiltersAudit[key].map((item: any) => item.name);
        }
      });
      if (Object.keys(tempLocalAuditQuestions).length > 0) {
        setLocalFilters({ ...tempLocalAuditQuestions });
      }
      if (tempAuditQuestions.length > 0) {
        tempFilters["Audit"] = tempAuditQuestions;
      }
      setFilters((prev: any) => {
        const merged = { ...prev, ...tempFilters };
        if (_.isEqual(prev, merged)) return prev;
        return merged;
      });
      setDisplayFilters((prev: any) => {
        const merged = { ...prev, ...tempDisplayFilters };
        if (_.isEqual(prev, merged)) return prev;
        return merged;
      });
      const tempExistsFiltersAudit = { ...existsFiltersAudit };
      if (!_.isEqual(filtersParams, tempExistsFiltersAudit)) {
        setFiltersParams({ ...tempExistsFiltersAudit });
      }
      setGegAudits(false);
    } else {
      setSearchQuery("");
    }
  }, [auditsFilter]);

  return (
    <FlexCol className="relative filters-date-no-remove">
      {customLoading && <LoadingOpBg />}
      <Form
        key={`${reRenderUserFilter ?? 0}-${JSON.stringify(defaultValues)}`}
        type="audit_filters_my_tasks"
        setFormData={setAuditsFilter}
        fieldsForm={fields}
        defaultValues={defaultValues}
        setDependenceFields={setDependenceFields}
        setGoldenRule={setGoldenRule}
      />
      {auditsFilter && Object.keys(auditsFilter).length > 0 && (
        <div
          onClick={handleResetAllFilters}
          className="p-5 flex flex-row group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
        >
          <div className="flex flex-col group-hover:underline">
            Сбросить фильтры
          </div>
          <div className="flex flex-col pt-1">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_33_4954)">
                <path
                  d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
                  fill="#637381"
                ></path>
              </g>
              <defs>
                <clipPath id="clip0_33_4954">
                  <rect
                    width="12"
                    height="12"
                    fill="white"
                    transform="translate(0 12) rotate(-90)"
                  ></rect>
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      )}
    </FlexCol>
  );
}

export function AuditsPage() {
  const [exportData, setExportData] = useState<any>({});
  const [filters, setFilters] = useState<any>({});
  const [localFilters, setLocalFilters] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [nextPage, setNextPage] = useState<any>(null);
  const [tempAudits, setTempAudits] = useState<any>([]);
  const [audits, setAudits] = useState<any>([]);
  const dispatch = useAppDispatch();
  const [customLoading, setCustomLoading] = useState(true);
  const [getAudits, setGegAudits] = useState(false);
  const location = useLocation();
  const statusUsers: string = useAppSelector(
    (state: RootState) => state.users.status
  );
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const users = useAppSelector(selectUsers);

  function handleSearch(value: string) {
    setSearchQuery(value);
  }

  const [displayFilters, setDisplayFilters] = useState<any>({});
  const [displayFiltersStr, setDisplayFiltersStr] = useState<any>("");
  const [reRenderFilterSettings, setReRenderFilterSettings] = useState(false);
  const [filtersParams, setFiltersParams] = useState({});
  const [defaultUserFilter, setDefaultUserFilter] = useState<any>(null);
  const [userFilterCheck, setUserFilterCheck] = useState(false);
  const [reRenderUserFilter, setReRenderUserFilter] = useState(null);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [auditsFilter, setAuditsFilter] = useState<any>(null);
  const [resetFilters, setResetFilters] = useState(false);
  const collections: any = useAppSelector((state) => selectItems(state, myTasksLocationsListName));
  const [favoriteFilters, setFavoriteFilters] = useState<any[]>([]);
  const [favoriteFiltersDone, setFavoriteFiltersDone] = useState<boolean>(false);

  // Эффект для начальной загрузки избранных фильтров
  useEffect(() => {
    if (collections.length === 0 || !currentUser?.id) return;
    if (favoriteFiltersDone) return;

    getItemsRequest({
      listName: MTFavoriteFiltersListName,
      filters: { Author: currentUser.id, Type: "audit" }
    }).then(results => {
      setFavoriteFilters([...results.items]);
      setFavoriteFiltersDone(true);
    })
  }, [collections, currentUser]);

useEffect(() => {
  if ( collections.length === 0 || !currentUser?.id) return;
  if (!favoriteFiltersDone && favoriteFilters.length === 0) return;
  if (Object.keys(filters).length > 0) return;

  const today = moment();
  const isMonday = today.isoWeekday() === 1;

  const defaultLocationIds = [
    ...(Array.isArray(currentUser.isLocationAdminMyTasks)
      ? currentUser.isLocationAdminMyTasks
      : []),
    ...(Array.isArray(currentUser.isLocationEditorMyTasks)
      ? currentUser.isLocationEditorMyTasks
      : []),
  ];

  const uniqueLocationIds = _.uniq(defaultLocationIds);

  const defaultFilters = {
    DateOb: {
      start: isMonday
        ? moment().subtract(1, "week").startOf("isoWeek").format("YYYY-MM-DDT00:00:00")
        : moment().startOf("isoWeek").format("YYYY-MM-DDT00:00:00"),
      end: isMonday
        ? moment().subtract(1, "week").endOf("isoWeek").format("YYYY-MM-DDT23:59:59")
        : moment().endOf("isoWeek").format("YYYY-MM-DDT23:59:59"),
    },
    ...(uniqueLocationIds.length > 0 && {
      Location: uniqueLocationIds,
    }),
  };

  const defaultFiltersFavorite = favoriteFilters.find((item: any) => item.DefaultFilter === true);
  let finalFilters;

  if (defaultFiltersFavorite) {
    const parsedFavorite = JSON.parse(defaultFiltersFavorite.MTFilters);
    finalFilters = { ...defaultFilters, ...parsedFavorite };
  } else {
    finalFilters = defaultFilters;
  }

  setFilters(finalFilters);
  setGegAudits(false);

}, [currentUser, collections, favoriteFiltersDone, favoriteFilters]);

  //Получение аудитов после изменения фильтров
  useEffect(() => {
    const controller = new AbortController();
    let canceled = false;
    if (!getAudits && Object.keys(filters).length > 0) {
      setAudits([]);
      setTempAudits([]);
      setCustomLoading(true);
      getItemsRequest({
        listName: myTasksAuditWithRelationsListName,
        rowLimit: 500,
        filters,
        sortOrder: { name: "ID", desc: true },
        signal: controller.signal,
      }).then((results: any) => {
        if (canceled) return;
        const { items, nextPage } = results;
        if (items.length > 0) {
          let emails: any = [];
          items.forEach((item: any) => {
            if (item.ObserverEmail) emails = [...emails, normalizeEmailLike(item.ObserverEmail)];
            if (item.ObservedUserEmail) emails = [...emails, normalizeEmailLike(item.ObservedUserEmail)];
          });
          dispatch(addUsers([..._.uniqWith(emails, _.isEqual)]));

          if (nextPage) {
            setNextPage(nextPage);
          } else {
            setNextPage(false);
          }

          setTempAudits([...items]);
          setGegAudits(true);
        } else {
          setGegAudits(true);
          setNextPage(false);
          setTempAudits([]);
          setAudits([]);
        }
      })
      .catch((error) => {
        if (canceled || error.name === 'AbortError' || error.name === 'CanceledError') return;
        setCustomLoading(false);
      });
    }
    return () => {
      canceled = true;
      controller.abort();
    };
  }, [filters]);

  //Получение аудитов по nextPage
  useEffect(() => {
    if (nextPage && getAudits) {
      getItemsRequest({
        listName: myTasksAuditWithRelationsListName,
        rowLimit: 500,
        filters,
        nextPage,
        sortOrder: { name: "ID", desc: true },
      }).then((results: any) => {
        const { items, nextPage } = results;
        let emails: any = [];
        items.forEach((item: any) => {
          if (item.ObserverEmail) emails = [...emails, normalizeEmailLike(item.ObserverEmail)];
          if (item.ObservedUserEmail) emails = [...emails, normalizeEmailLike(item.ObservedUserEmail)];
        });
        dispatch(addUsers([..._.uniqWith(emails, _.isEqual)]));
        setTempAudits((prevState: any) => [...prevState, ...items]);
        if (nextPage) {
          setNextPage(nextPage);
        } else {
          setNextPage(false);
        }
      });
    }
  }, [nextPage]);

  useEffect(() => {
    let auditsWithQuestions: any = [];
    tempAudits.forEach((audit: any) => {
      if (audit.Audit) {
        auditsWithQuestions = [
          ...auditsWithQuestions,
          { ...audit, ...JSON.parse(audit.Audit) },
        ];
      }
    });

    let localTempAudits: any = [...auditsWithQuestions];
    if (Object.keys(localFilters).length > 0) {
      Object.keys(localFilters).forEach((key) => {
        localTempAudits = localTempAudits.filter((audit: any) => {
          if (Array.isArray(localFilters[key])) {
            return localFilters[key].some(
              (localFilter: any) =>
                audit.Audit &&
                audit.Audit.indexOf((localFilter.name || "").trim()) !== -1
            );
          }
          return false;
        });
      });
    }

    const normalizedSearchQuery = normalizeSearchValue(searchQuery);
    if (normalizedSearchQuery) {
      localTempAudits = localTempAudits.filter((audit: any) =>
        getAuditSearchHaystack(audit, users).includes(normalizedSearchQuery)
      );
    }

    setAudits([..._.uniqWith(localTempAudits, _.isEqual)]);
  }, [tempAudits, localFilters, searchQuery, users]);

  useEffect(() => {
    const auditCardID = new URLSearchParams(location.search).get("auditCardMobile");
    if (auditCardID && safeStorageGet("NEW_AUDIT")) {
      safeStorageSet("NEW_AUDIT", "");
      setGegAudits(false);
    }
  }, [location]);

  useEffect(() => {
    if (!nextPage && getAudits) {
      setCustomLoading(false);
    }
  }, [audits]);

  useEffect(() => {
    let tempStr = "";
    Object.keys(displayFilters).map((key: string, idx: number) => {
      if (idx <= 3) {
        if (Array.isArray(displayFilters[key])) {
          displayFilters[key].map((item: string, idx: number) => {
            if (displayFilters[key].length - 1 === idx) {
              tempStr += item
            } else {
              tempStr += `${item}, `
            }
          })
        } else {
          tempStr += `${displayFilters[key]}`
        }
        tempStr += ', '
      }
    });
    tempStr = tempStr.slice(0, -2);
    if (Object.keys(displayFilters).length > 4) {
      tempStr += `...ещё ${Object.keys(displayFilters).length - 4}`
    }
    setDisplayFiltersStr(tempStr);
  }, [displayFilters]);

  const isSelectedFilters = useMemo(() => {
    return (
      (filters.DateOb && Object.keys(filters.DateOb).length > 0) || // Дата наблюдения
      (filters.Location && filters.Location.length > 0) || // Локация
      (filters.ObserverEmail && filters.ObserverEmail.trim() !== '') || // ФИО наблюдателя
      (filters.ObservedUserEmail && filters.ObservedUserEmail.trim() !== '') || // ФИО за кем проводили наблюдение
      (filters.Department && filters.Department.length > 0) || // Отдел где проводилось наблюдение
      (filters.Region && filters.Region.length > 0) || // Участок где проводилось наблюдение
      (filters.Audit && filters.Audit.length > 0) // Аудит проводился совместно с наставником | 5S | Золотые правила | Риски | SAFE
    )
  }, [filters]);

  const extraCompareData = useMemo(() => {
    const today = moment();
    const isMonday = today.isoWeekday() === 1;
    const defaultDateRange = {
      startDate: isMonday
        ? moment().subtract(1, "weeks").startOf("isoWeek").format('YYYY-MM-DDT00:00:00')
        : moment().startOf("isoWeek").format('YYYY-MM-DDT00:00:00'),
      endDate: isMonday
        ? moment().subtract(1, "weeks").endOf("isoWeek").format('YYYY-MM-DDT23:59:59')
        : moment().endOf("isoWeek").format('YYYY-MM-DDT23:59:59'),
    };
    const result: any = {};
    if (auditsFilter?.DateOb && !_.isEqual(auditsFilter.DateOb, defaultDateRange)) {
      result.DateOb = auditsFilter.DateOb;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }, [auditsFilter]);

  function handleResetAllFilters() {
    const defaultFiltersFavorite = favoriteFilters.find((item: any) => item.DefaultFilter === true);
    setDefaultUserFilter(defaultFiltersFavorite || null);
    setLocalFilters({});
    setFilters({});
    setDisplayFilters({});
    setFiltersParams({});
    setAuditsFilter(null);
    setResetFilters(!resetFilters);
    setGegAudits(false);
  }

  return (
    <FlexRow className="scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-screen">
      <FlexCol className="bg-white sticky-custom border-r w-80 text-[14px] text-[#8899A8] relative scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] max-h-screen hidden">
        <FlexCol className="max-w-[320px] overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
          <FilterActions
            isOpen={isOpenFilter}
            onClose={() => setIsOpenFilter(false)}
            onResetFilters={handleResetAllFilters}
            filters={filters}
            displayFilters={displayFilters}
            reRenderFilterSettings={reRenderFilterSettings}
            setReRenderFilterSettings={setReRenderFilterSettings}
            filtersParams={filtersParams}
            favoriteType="audit"
            favoriteListQuestions={myTasksAuditWithRelationsListName}
            reRenderTableFlag={setGegAudits}
            setFiltersParams={setFiltersParams}
            setDefaultUserFilter={setDefaultUserFilter}
            setUserFilterCheck={setUserFilterCheck}
            userFilterCheck={userFilterCheck}
            setReRenderUserFilter={setReRenderUserFilter}
            favoriteFilters={favoriteFilters}
            setFavoriteFilters={setFavoriteFilters}
            extraCompareData={extraCompareData}
          >
            <AuditsFilters
              filters={filters}
              setGegAudits={setGegAudits}
              setFilters={setFilters}
              setSearchQuery={setSearchQuery}
              searchQuery={searchQuery}
              setLocalFilters={setLocalFilters}
              currentUser={currentUser}
              setDisplayFilters={setDisplayFilters}
              filtersParams={filtersParams}
              setFiltersParams={setFiltersParams}
              defaultUserFilter={defaultUserFilter}
              userFilterCheck={userFilterCheck}
              reRenderUserFilter={reRenderUserFilter}
              handleResetAllFilters={handleResetAllFilters}
              auditsFilter={auditsFilter}
              setAuditsFilter={setAuditsFilter}
              resetFilters={resetFilters}
              setResetFilters={setResetFilters}
            />
          </FilterActions>
        </FlexCol>
      </FlexCol>
      <FlexCol className="grow w-[calc(100%-320px)]">
        <FlexCol className="w-full">
          <FlexRow className="px-0 tablet:px-7.5 h-auto justify-between items-center shrink-0">
            <FlexCol>
              <nav
                className="flex justify-between w-full"
                aria-label="Breadcrumb"
              >
                <FlexCol className="grow">
                  <ol className="inline-flex items-center">
                    <li
                      className="inline-flex items-center"
                      aria-current="page"
                    >
                      <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
                        <span>Поведенческий аудит (SBO)</span>
                      </div>
                    </li>
                  </ol>
                </FlexCol>
              </nav>
            </FlexCol>
          </FlexRow>
          <FlexCol className="pt-3.5 pb-3.5 gap-[16px]">
            <FlexRow className="grow gap-[10px]">
              <SearchUI
                placeholder="Ключевая фраза"
                defaultValue={searchQuery}
                onClick={handleSearch}
                inputClass="text-[13px]"
              />
              <FiltersButton
                isSelectedFilters={isSelectedFilters}
                onOpenFilters={() => { setIsOpenFilter(true); setReRenderUserFilter(Math.random()); }}
              />
            </FlexRow>
            <FlexCol className="shring-0">
              {(currentUser.isLocationEditorMyTasks ||
                currentUser.isAdmin ||
                currentUser.isLocationAdminMyTasks) && <AddAudit zIndex={70} />}
            </FlexCol>
          </FlexCol>
          <FlexRow>
            {(customLoading || statusUsers === "LOADING") && audits.length === 0 ? (
              <FlexCol className="w-full py-10 items-center justify-center gap-3">
                <Loading />
              </FlexCol>
            ) : (
              <MyTasksAuditsTable
                data={audits}
                setExportData={setExportData}
                customLoading={customLoading || statusUsers === "LOADING"}
                nextPage={nextPage}
              />
            )}
          </FlexRow>
        </FlexCol>
      </FlexCol>
    </FlexRow>
  );
}
